namespace $ {
	export class $visavis_app extends $mol_book2 {
		
		/**
		 * ```tree
		 * attr * mol_theme \$mol_theme_light
		 * ```
		 */
		attr() {
			return {
				mol_theme: "$mol_theme_light"
			} as Record< string, any >
		}
		
		/**
		 * ```tree
		 * pages /
		 * 	<= Menu
		 * 	<= Matrix*plot
		 * 	<= Cube*plot
		 * 	<= Phase*plot
		 * 	<= Bar*plot
		 * 	<= Discovery*plot
		 * 	<= Eigen*plot
		 * 	<= Pie*plot
		 * 	<= Scatter*plot
		 * 	<= Customscatter*plot
		 * 	<= Heatmap*plot
		 * 	<= Graph*plot
		 * ```
		 */
		pages() {
			return [
				this.Menu(),
				this.Matrix("plot"),
				this.Cube("plot"),
				this.Phase("plot"),
				this.Bar("plot"),
				this.Discovery("plot"),
				this.Eigen("plot"),
				this.Pie("plot"),
				this.Scatter("plot"),
				this.Customscatter("plot"),
				this.Heatmap("plot"),
				this.Graph("plot")
			] as readonly any[]
		}
		
		/**
		 * ```tree
		 * files_read? null
		 * ```
		 */
		@ $mol_mem
		files_read(next?: any) {
			if ( next !== undefined ) return next as never
			return null as any
		}
		
		/**
		 * ```tree
		 * Upload $mol_button_open
		 * 	hint @ \Upload a plot from MPDS in JSON
		 * 	accept \application/json
		 * 	files? <=> files_read?
		 * ```
		 */
		@ $mol_mem
		Upload() {
			const obj = new this.$.$mol_button_open()
			
			obj.hint = () => this.$.$mol_locale.text( '$visavis_app_Upload_hint' )
			obj.accept = () => "application/json"
			obj.files = (next?: any) => this.files_read(next)
			
			return obj
		}
		
		/**
		 * ```tree
		 * plot_id* \
		 * ```
		 */
		plot_id(id: any) {
			return ""
		}
		
		/**
		 * ```tree
		 * Plot_link* $mol_link
		 * 	arg * file <= plot_id*
		 * 	title <= plot_id*
		 * ```
		 */
		@ $mol_mem_key
		Plot_link(id: any) {
			const obj = new this.$.$mol_link()
			
			obj.arg = () => ({
				file: this.plot_id(id)
			} as Record< string, any >)
			obj.title = () => this.plot_id(id)
			
			return obj
		}
		
		/**
		 * ```tree
		 * history_drop*? null
		 * ```
		 */
		@ $mol_mem_key
		history_drop(id: any, next?: any) {
			if ( next !== undefined ) return next as never
			return null as any
		}
		
		/**
		 * ```tree
		 * Plot_drop_icon* $mol_icon_delete
		 * ```
		 */
		@ $mol_mem_key
		Plot_drop_icon(id: any) {
			const obj = new this.$.$mol_icon_delete()
			
			return obj
		}
		
		/**
		 * ```tree
		 * Plot_drop* $mol_button_minor
		 * 	click? <=> history_drop*?
		 * 	sub / <= Plot_drop_icon*
		 * ```
		 */
		@ $mol_mem_key
		Plot_drop(id: any) {
			const obj = new this.$.$mol_button_minor()
			
			obj.click = (next?: any) => this.history_drop(id, next)
			obj.sub = () => [
				this.Plot_drop_icon(id)
			] as readonly any[]
			
			return obj
		}
		
		/**
		 * ```tree
		 * Plot*0 $mol_view sub /
		 * 	<= Plot_link*
		 * 	<= Plot_drop*
		 * ```
		 */
		@ $mol_mem_key
		Plot(id: any) {
			const obj = new this.$.$mol_view()
			
			obj.sub = () => [
				this.Plot_link(id),
				this.Plot_drop(id)
			] as readonly any[]
			
			return obj
		}
		
		/**
		 * ```tree
		 * history_rows / <= Plot*0
		 * ```
		 */
		history_rows() {
			return [
				this.Plot("0")
			] as readonly any[]
		}
		
		/**
		 * ```tree
		 * History $mol_list
		 * 	title @ \Uploads
		 * 	rows <= history_rows
		 * ```
		 */
		@ $mol_mem
		History() {
			const obj = new this.$.$mol_list()
			
			obj.title = () => this.$.$mol_locale.text( '$visavis_app_History_title' )
			obj.rows = () => this.history_rows()
			
			return obj
		}
		
		/**
		 * ```tree
		 * example_title* \
		 * ```
		 */
		example_title(id: any) {
			return ""
		}
		
		/**
		 * ```tree
		 * Example*0 $mol_link
		 * 	arg * file <= example_title*
		 * 	title <= example_title*
		 * ```
		 */
		@ $mol_mem_key
		Example(id: any) {
			const obj = new this.$.$mol_link()
			
			obj.arg = () => ({
				file: this.example_title(id)
			} as Record< string, any >)
			obj.title = () => this.example_title(id)
			
			return obj
		}
		
		/**
		 * ```tree
		 * example_rows / <= Example*0
		 * ```
		 */
		example_rows() {
			return [
				this.Example("0")
			] as readonly any[]
		}
		
		/**
		 * ```tree
		 * Examples $mol_list
		 * 	title @ \Examples
		 * 	rows <= example_rows
		 * ```
		 */
		@ $mol_mem
		Examples() {
			const obj = new this.$.$mol_list()
			
			obj.title = () => this.$.$mol_locale.text( '$visavis_app_Examples_title' )
			obj.rows = () => this.example_rows()
			
			return obj
		}
		
		/**
		 * ```tree
		 * Deck $mol_deck items /
		 * 	<= History
		 * 	<= Examples
		 * ```
		 */
		@ $mol_mem
		Deck() {
			const obj = new this.$.$mol_deck()
			
			obj.items = () => [
				this.History(),
				this.Examples()
			] as readonly any[]
			
			return obj
		}
		
		/**
		 * ```tree
		 * Source $mol_link_source uri \https://github.com/mpds-io/visavis
		 * ```
		 */
		@ $mol_mem
		Source() {
			const obj = new this.$.$mol_link_source()
			
			obj.uri = () => "https://github.com/mpds-io/visavis"
			
			return obj
		}
		
		/**
		 * ```tree
		 * Menu $mol_page
		 * 	title \Vis-a-vis
		 * 	tools / <= Upload
		 * 	body / <= Deck
		 * 	foot / <= Source
		 * ```
		 */
		@ $mol_mem
		Menu() {
			const obj = new this.$.$mol_page()
			
			obj.title = () => "Vis-a-vis"
			obj.tools = () => [
				this.Upload()
			] as readonly any[]
			obj.body = () => [
				this.Deck()
			] as readonly any[]
			obj.foot = () => [
				this.Source()
			] as readonly any[]
			
			return obj
		}
		
		/**
		 * ```tree
		 * plot* $visavis_plot
		 * ```
		 */
		@ $mol_mem_key
		plot(id: any) {
			const obj = new this.$.$visavis_plot()
			
			return obj
		}
		
		/**
		 * ```tree
		 * Matrix*plot $visavis_matrix plot <= plot*
		 * ```
		 */
		@ $mol_mem_key
		Matrix(id: any) {
			const obj = new this.$.$visavis_matrix()
			
			obj.plot = () => this.plot(id)
			
			return obj
		}
		
		/**
		 * ```tree
		 * Cube*plot $visavis_cube plot <= plot*
		 * ```
		 */
		@ $mol_mem_key
		Cube(id: any) {
			const obj = new this.$.$visavis_cube()
			
			obj.plot = () => this.plot(id)
			
			return obj
		}
		
		/**
		 * ```tree
		 * Phase*plot $visavis_phase plot <= plot*
		 * ```
		 */
		@ $mol_mem_key
		Phase(id: any) {
			const obj = new this.$.$visavis_phase()
			
			obj.plot = () => this.plot(id)
			
			return obj
		}
		
		/**
		 * ```tree
		 * Bar*plot $visavis_bar plot <= plot*
		 * ```
		 */
		@ $mol_mem_key
		Bar(id: any) {
			const obj = new this.$.$visavis_bar()
			
			obj.plot = () => this.plot(id)
			
			return obj
		}
		
		/**
		 * ```tree
		 * Discovery*plot $visavis_discovery plot <= plot*
		 * ```
		 */
		@ $mol_mem_key
		Discovery(id: any) {
			const obj = new this.$.$visavis_discovery()
			
			obj.plot = () => this.plot(id)
			
			return obj
		}
		
		/**
		 * ```tree
		 * Eigen*plot $visavis_eigen plot <= plot*
		 * ```
		 */
		@ $mol_mem_key
		Eigen(id: any) {
			const obj = new this.$.$visavis_eigen()
			
			obj.plot = () => this.plot(id)
			
			return obj
		}
		
		/**
		 * ```tree
		 * Pie*plot $visavis_pie plot <= plot*
		 * ```
		 */
		@ $mol_mem_key
		Pie(id: any) {
			const obj = new this.$.$visavis_pie()
			
			obj.plot = () => this.plot(id)
			
			return obj
		}
		
		/**
		 * ```tree
		 * Scatter*plot $visavis_scatter plot <= plot*
		 * ```
		 */
		@ $mol_mem_key
		Scatter(id: any) {
			const obj = new this.$.$visavis_scatter()
			
			obj.plot = () => this.plot(id)
			
			return obj
		}
		
		/**
		 * ```tree
		 * Customscatter*plot $visavis_customscatter plot <= plot*
		 * ```
		 */
		@ $mol_mem_key
		Customscatter(id: any) {
			const obj = new this.$.$visavis_customscatter()
			
			obj.plot = () => this.plot(id)
			
			return obj
		}
		
		/**
		 * ```tree
		 * Heatmap*plot $visavis_heatmap plot <= plot*
		 * ```
		 */
		@ $mol_mem_key
		Heatmap(id: any) {
			const obj = new this.$.$visavis_heatmap()
			
			obj.plot = () => this.plot(id)
			
			return obj
		}
		
		/**
		 * ```tree
		 * Graph*plot $visavis_graph plot <= plot*
		 * ```
		 */
		@ $mol_mem_key
		Graph(id: any) {
			const obj = new this.$.$visavis_graph()
			
			obj.plot = () => this.plot(id)
			
			return obj
		}
	}
	
}

